import { useMemo, useState } from "react"
import { createFileRoute } from "@tanstack/react-router"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import { Mail, Plus, X } from "lucide-react"
import { useActiveEvent } from "@/core/active-event"
import {
  eventRolesQueryOptions,
  inviteEventCrew,
  removeEventRole,
  type EventRole,
  type EventRoleRow,
} from "@/core/settings"
import { listEventStaff, type EventStaff } from "@/core/channels"
import { ApiError } from "@/core/api"
import { Avatar } from "@/components/ui/Avatar"
import {
  Card,
  GhostButton,
  Pill,
  Select,
  SectionPage,
  TextInput,
} from "@/components/settings/primitives"

export const Route = createFileRoute("/$orgSlug/$eventSlug/settings/permissions")({
  component: PermissionsView,
})

const ROLE_SECTIONS: { role: EventRole; title: string; blurb: string }[] = [
  {
    role: "organizer",
    title: "Organizers",
    blurb:
      "Full write access to this event. Plain org members must be an organizer here to manage it.",
  },
  {
    role: "contractor",
    title: "Contractors",
    blurb: "Read-only event access — schedule and channels, no edits.",
  },
]

/** react-query key for the event staff roster, shared with the channels UI. */
const staffKey = (eventId: string) => ["event-staff", eventId] as const

function PermissionsView() {
  const { eventId } = useActiveEvent()
  const qc = useQueryClient()

  const rolesQ = useQuery({ ...eventRolesQueryOptions(eventId ?? ""), enabled: !!eventId })
  // Everyone who can work this event: org members PLUS external crew, who hold
  // no org seat. The old page read BA's org member list alone, so an external
  // crew member's row here rendered as a bare user id with no name or email.
  const staffQ = useQuery({
    queryKey: staffKey(eventId ?? ""),
    enabled: !!eventId,
    queryFn: () => listEventStaff(eventId!),
  })

  const roles = rolesQ.data ?? []
  const staff = staffQ.data ?? []
  // event_roles CRUD is org-role gated (read: member+, delete: admin/owner) and
  // root-mounted, so an EXTERNAL organizer — who can invite via the event-scoped
  // action — gets a 403 here. That is not a page failure: the roster already
  // says who holds which role; the CRUD rows only add the ids removal needs.
  // ponytail: external organizers can add but not remove — needs an
  // event-scoped revoke action (route registration = compile) to lift.
  const rolesDenied = rolesQ.isError && (rolesQ.error as ApiError)?.status === 403

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["resource", "event_roles", { eventId }] })
    if (eventId) qc.invalidateQueries({ queryKey: staffKey(eventId) })
  }

  return (
    <SectionPage
      title="Permissions"
      description="Grant per-event roles — to people in this organization, or to outside crew by email."
    >
      {((rolesQ.isError && !rolesDenied) || staffQ.isError) && (
        <Card>
          <p className="text-[13px] text-rose-600 dark:text-rose-400">
            {((staffQ.error ?? rolesQ.error) as Error)?.message ?? "Failed to load permissions"}
          </p>
        </Card>
      )}

      {ROLE_SECTIONS.map((s) => (
        <RoleSection
          key={s.role}
          eventId={eventId}
          role={s.role}
          title={s.title}
          blurb={s.blurb}
          roles={roles}
          rolesDenied={rolesDenied}
          staff={staff}
          onChanged={invalidate}
        />
      ))}
    </SectionPage>
  )
}

function RoleSection({
  eventId,
  role,
  title,
  blurb,
  roles,
  rolesDenied,
  staff,
  onChanged,
}: {
  eventId: string | undefined
  role: EventRole
  title: string
  blurb: string
  roles: EventRoleRow[]
  rolesDenied: boolean
  staff: EventStaff[]
  onChanged: () => void
}) {
  const [pick, setPick] = useState("")
  const [inviteName, setInviteName] = useState("")
  const [inviteEmail, setInviteEmail] = useState("")

  // Who holds this role. With CRUD rows: those (they carry the id removal
  // needs). Without them (external viewer, 403): the roster's own role field —
  // listEventStaff already reports event_roles over org membership.
  const assigned = useMemo<Array<{ id: string | null; linkedUserId: string }>>(
    () =>
      rolesDenied
        ? staff.filter((m) => m.role === role).map((m) => ({ id: null, linkedUserId: m.userId }))
        : roles.filter((r) => r.role === role),
    [roles, rolesDenied, staff, role],
  )
  const assignedUserIds = useMemo(
    () => new Set(assigned.map((r) => r.linkedUserId)),
    [assigned],
  )
  // Only people with an org seat can be added by picker — external crew have no
  // account to pick until they've been invited by email. `!== null` (not
  // `!= null`) on purpose: a backend predating `orgRole` omits the field
  // entirely, and treating undefined as "external" would silently empty the
  // picker if this bundle ships ahead of the API.
  const available = staff.filter(
    (m) => m.orgRole !== null && !assignedUserIds.has(m.userId),
  )

  const staffById = useMemo(() => {
    const map = new Map<string, EventStaff>()
    for (const m of staff) map.set(m.userId, m)
    return map
  }, [staff])

  // Adding an existing colleague goes through inviteEventCrew as well, not
  // event_roles CRUD: `eventId` and `organizationId` are both ctx-bound
  // firewall fields and therefore systemManaged, and the generated validator
  // rejects those keys outright — the CRUD create path 400s no matter what
  // the body contains. inviteEventCrew find-or-creates, so for someone who
  // already has an account it simply grants the role.
  const add = useMutation({
    mutationFn: () => {
      if (!eventId || !pick) throw new ApiError("Pick a member", 400)
      const m = staffById.get(pick)
      if (!m?.email) throw new ApiError("That member has no email on file", 400)
      // notify:false — they already have a seat and an account; no sign-in mail.
      return inviteEventCrew({ eventId, email: m.email, name: m.name ?? m.email, role, notify: false })
    },
    onSuccess: (r) => {
      setPick("")
      toast.success(
        r.alreadyCrew
          ? "Already has that role"
          : `Added ${title.slice(0, -1).toLowerCase()}`,
      )
      onChanged()
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : "Failed to add role"),
  })

  const invite = useMutation({
    mutationFn: () => {
      if (!eventId) throw new ApiError("No active event", 400)
      const name = inviteName.trim()
      const email = inviteEmail.trim()
      if (!name) throw new ApiError("Add a name", 400)
      if (!email.includes("@")) throw new ApiError("Add a valid email", 400)
      return inviteEventCrew({ eventId, email, name, role })
    },
    onSuccess: (r) => {
      setInviteName("")
      setInviteEmail("")
      if (r.alreadyCrew) {
        toast.info("Already on the crew — no email sent")
      } else if (r.emailStatus && r.emailStatus !== "sent") {
        // The role is live regardless; only the mail failed or was redirected
        // (sandbox plans send to the host). Say so instead of a silent success.
        toast.warning(`Added, but the invite email was ${r.emailStatus}`)
      } else {
        toast.success("Invite sent")
      }
      onChanged()
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : "Failed to invite crew"),
  })

  const remove = useMutation({
    mutationFn: (roleId: string) => removeEventRole(roleId),
    onSuccess: () => {
      toast.success("Removed")
      onChanged()
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : "Failed to remove role"),
  })

  return (
    <Card title={title} description={blurb}>
      <div className="flex flex-wrap items-center gap-2">
        <Select
          className="flex-1"
          value={pick}
          onChange={(e) => setPick(e.target.value)}
          disabled={available.length === 0}
        >
          <option value="">
            {available.length === 0 ? "No more members to add" : "Select a member…"}
          </option>
          {available.map((m) => (
            <option key={m.userId} value={m.userId}>
              {m.name ?? m.email ?? m.userId}
            </option>
          ))}
        </Select>
        <GhostButton onClick={() => add.mutate()} disabled={!pick || add.isPending}>
          <Plus className="h-4 w-4" />
          Add
        </GhostButton>
      </div>

      <div className="space-y-2 rounded-xl border border-dashed border-plum-200/70 p-3 dark:border-white/10">
        <p className="text-[12.5px] text-plum-700/60 dark:text-plum-200/45">
          Not in this organization? Invite them by email — they'll get sign-in
          instructions and reach this event only.
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <TextInput
            className="min-w-[9rem] flex-1"
            placeholder="Name"
            value={inviteName}
            onChange={(e) => setInviteName(e.target.value)}
          />
          <TextInput
            className="min-w-[12rem] flex-1"
            type="email"
            placeholder="name@company.com"
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
          />
          <GhostButton
            onClick={() => invite.mutate()}
            disabled={!inviteName.trim() || !inviteEmail.trim() || invite.isPending}
          >
            <Mail className="h-4 w-4" />
            Invite
          </GhostButton>
        </div>
      </div>

      {assigned.length === 0 ? (
        <p className="text-[12.5px] text-plum-700/45 dark:text-plum-200/35">
          No {title.toLowerCase()} yet.
        </p>
      ) : (
        <ul className="space-y-1.5">
          {assigned.map((r) => {
            const m = staffById.get(r.linkedUserId)
            return (
              <li
                key={r.id ?? r.linkedUserId}
                className="flex items-center gap-2.5 rounded-xl border border-plum-200/60 px-3 py-2 dark:border-white/10"
              >
                <Avatar
                  name={m?.name ?? m?.email ?? "?"}
                  kind="organizer"
                  userId={r.linkedUserId}
                  size={28}
                />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[13px] font-semibold text-plum-900 dark:text-plum-50">
                    {m?.name ?? m?.email ?? r.linkedUserId}
                  </p>
                  {m?.name && m.email && (
                    <p className="truncate text-[11.5px] text-plum-700/50 dark:text-plum-200/35">
                      {m.email}
                    </p>
                  )}
                </div>
                {/* Only claim a seat when we actually resolved their row —
                    during the roster load, and for rows an external viewer
                    can't see, "External" would be a guess, not a fact. */}
                {m && <Pill>{m.orgRole ?? "External"}</Pill>}
                {r.id && (
                <button
                  type="button"
                  onClick={() => remove.mutate(r.id!)}
                  disabled={remove.isPending}
                  className="grid h-7 w-7 shrink-0 place-items-center rounded-lg text-plum-700/50 transition hover:bg-rose-50 hover:text-rose-600 disabled:opacity-50 dark:text-plum-200/40 dark:hover:bg-rose-500/10"
                  title="Remove"
                >
                  <X className="h-4 w-4" />
                </button>
                )}
              </li>
            )
          })}
        </ul>
      )}
    </Card>
  )
}
